import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { cn } from "../utils";
import { Skeleton } from "./Skeleton";

// Placeholder width ranges per column type, as [min, max] percentages
// (or px for the index column).
const RANGES = {
  index: [14, 24],
  status: [64, 84],
  id: [70, 90],
  text: [50, 80],
  number: [35, 60],
};

/** Best-effort column type from a column definition's key/header text. */
export function inferColumnType(col = {}) {
  if (col.type) return col.type;
  const text = String(col.header ?? col.label ?? col.title ?? col.key ?? "").trim();
  if (col.checkbox) return "checkbox";
  if (/^(#|s\.?\s?no\.?|sl\.?\s?no\.?|sr\.?\s?no\.?|serial)$/i.test(text)) return "index";
  if (/^actions?$/i.test(text)) return "actions";
  if (/status|state$/i.test(text)) return "status";
  if (/date|time|count|weight|amount|qty|quantity|total|price|cod\b|value|aging|attempts?|charges?|fees?|days?|hours?|percent|%/i.test(text)) return "number";
  if (/\b(id|ids|awb|mawb|waybill|tracking|code|no\.?|number|ref)\b|Id$/.test(text) || /tracking|waybill/i.test(text)) return "id";
  return "text";
}

/** Reads the last header row of the table that owns `tbody`. */
function readHeaderColumns(tbody) {
  const table = tbody?.closest("table");
  const headRows = table?.tHead?.rows;
  const row = headRows?.[headRows.length - 1];
  if (!row) return null;

  const cells = Array.from(row.cells);
  // Copy the real cell padding so skeleton rows line up with real rows.
  const style = cells[0] ? window.getComputedStyle(cells[0]) : null;
  const padding = style ? `0 ${style.paddingRight} 0 ${style.paddingLeft}` : undefined;

  const columns = [];
  cells.forEach((cell) => {
    const hasCheckbox = !!cell.querySelector('input[type="checkbox"], [role="checkbox"]');
    const header = cell.textContent.replace(/\s+/g, " ").trim();
    const col = { header, checkbox: hasCheckbox, type: hasCheckbox ? "checkbox" : undefined };
    for (let i = 0; i < (cell.colSpan || 1); i += 1) columns.push(col);
  });
  return { columns, padding };
}

function seededWidths(columns, rows) {
  return Array.from({ length: rows }, () =>
    columns.map((col) => {
      const range = RANGES[inferColumnType(col)];
      if (!range) return null;
      const [min, max] = range;
      return Math.round(min + Math.random() * (max - min));
    })
  );
}

function CellPlaceholder({ type, width }) {
  switch (type) {
    case "checkbox":
      return <Skeleton variant="box" />;
    case "index":
      return <Skeleton variant="line" width={width} />;
    case "status":
      return <Skeleton variant="pill" width={width} />;
    case "actions":
      return (
        <span className="flex items-center justify-center gap-2">
          <Skeleton variant="box" />
          <Skeleton variant="box" />
        </span>
      );
    default:
      return <Skeleton variant="line" width={`${width}%`} />;
  }
}

/**
 * Skeleton `<tbody>` that goes inside an existing `<table>`, after the real
 * `<thead>`. With no `columns` it reads the header cells from the DOM, so
 * hand-written table heads work without a column config.
 */
export function TableSkeletonBody({ columns: columnsProp, rows = 10, className }) {
  const ref = useRef(null);
  const [measured, setMeasured] = useState(null);

  useLayoutEffect(() => {
    if (columnsProp) return;
    setMeasured(readHeaderColumns(ref.current));
  }, [columnsProp]);

  const columns = columnsProp || measured?.columns || [];
  const signature = columns.map((c) => inferColumnType(c)).join("|");
  // Random widths are fixed for the life of this mount, so rows look organic
  // without flickering on re-render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const widths = useMemo(() => seededWidths(columns, rows), [signature, rows]);

  return (
    <tbody ref={ref} aria-hidden="true" className={cn("aone-skel-body", className)}>
      {columns.length === 0 ? (
        <tr />
      ) : (
        widths.map((rowWidths, r) => (
          <tr key={r} style={{ opacity: Math.max(0, 1 - r * 0.07) }}>
            {columns.map((col, c) => {
              const type = inferColumnType(col);
              return (
                <td
                  key={c}
                  className={cn(!measured?.padding && "px-4", "align-middle")}
                  style={measured?.padding ? { padding: measured.padding } : undefined}
                >
                  <CellPlaceholder type={type} width={rowWidths[c]} />
                </td>
              );
            })}
          </tr>
        ))
      )}
    </tbody>
  );
}

/**
 * Stand-alone table skeleton for tables driven by a column config.
 * columns: [{ key, header, type? }]. Renders the real header row so it
 * never disappears while loading.
 */
export function TableSkeleton({ columns = [], rows = 10, label = "Loading", className }) {
  return (
    <div role="status" aria-live="polite" aria-label={label} className={className}>
      <table className="min-w-full w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col, i) => (
              <th
                key={col.key ?? i}
                className="px-4 py-3 text-left text-xs font-medium text-gray-700 whitespace-nowrap border-b border-solid border-gray-200 border-x-0 border-t-0"
              >
                {inferColumnType(col) === "checkbox" ? <Skeleton variant="box" /> : col.header}
              </th>
            ))}
          </tr>
        </thead>
        <TableSkeletonBody columns={columns} rows={rows} />
      </table>
    </div>
  );
}

export default TableSkeleton;
